// Discover row identity. Every row is one pool (operator rule 2026-09-30): a
// token's main row — its primary TON pool, or its bonding curve — is keyed by
// the jetton master, so a launchpad migration keeps its row; a pool without
// TON is its own row, keyed by the pool address. Mirrors the backend's
// TokenSummary.RowKey. Membership, deltas, dedup and live routing key on this,
// never on the master alone; token-level facts (metadata, birth) still fan out
// by master.

import type { TokenSummary, TradeSummary } from '../api/types'
import { isTonQuoteToken } from './tonQuote'

export function rowKeyOf(row: Pick<TokenSummary, 'row_key' | 'jetton_master'>): string {
  return row.row_key || row.jetton_master
}

/** The token's main row (keyed by its master), as opposed to a pool row. */
export function isMainTokenRow(row: Pick<TokenSummary, 'row_key' | 'jetton_master'>): boolean {
  return rowKeyOf(row) === row.jetton_master
}

/** A trade quoted in something other than TON belongs to its pool's own row. */
export function isNonTonQuotedTrade(trade: Pick<TradeSummary, 'quote_token'>): boolean {
  return Boolean(trade.quote_token) && !isTonQuoteToken(trade.quote_token)
}

/**
 * The row a live trade belongs to: the token's main row for a TON-quoted
 * trade (or one whose producer did not say), else its pool's own row. A wrong
 * guess is harmless — the server answers a delta for a row it does not hold
 * with a removal of a key the board does not have.
 */
export function tradeRowKey(
  trade: Pick<TradeSummary, 'base_token' | 'pool_addr' | 'quote_token'>
): string {
  if (isNonTonQuotedTrade(trade)) return trade.pool_addr ?? ''
  return trade.base_token || trade.pool_addr || ''
}
